import { useState } from "react"
import { Divider, MenuItem, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import MenuPopover from "minimals-template/components/MenuPopover"
import { IconButtonAnimate } from "minimals-template/components/animate"
import { useDialog } from "lib/@hooks/useDialog"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { useContentLanguage } from "./content-language-context"
import { getContentLanguages } from "./controller/get-content-languages"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { ContentLanguageItem } from "./content-language-item"
import { AddLanguageDialog } from "./add-language-dialog"
import { LanguageOption } from "./language-option"
import { clearTranslationCache } from "./controller/clear-translation-cache"
import { hasDemand } from "lib/authorization/has-demand"
import { useSnackbar } from "notistack"

const ENGLISH = { code: "en", name: "English", nativeName: "English", rtl: false }

export function ContentLanguageSelector() {
    const { language, languageName, setLanguage } = useContentLanguage()
    const rawLanguages = getContentLanguages.useResults(withInitialValue(IS_LOADING))
    const languages = rawLanguages === IS_LOADING ? [] : rawLanguages ?? []
    const [open, setOpen] = useState(null)
    const openAddDialog = useDialog(<AddLanguageDialog />)
    const { enqueueSnackbar } = useSnackbar()

    const isTranslating = language !== "en"
    const isAdmin = hasDemand("sfg20Admin")

    return (
        <>
            <IconButtonAnimate
                aria-label="change content language"
                onClick={handleOpen}
                sx={{
                    width: 40,
                    height: 40,
                    ...(open && { bgcolor: "action.selected" }),
                }}
            >
                <Iconify icon="mdi:translate" sx={{ color: isTranslating ? "primary.main" : "text.secondary" }} />
            </IconButtonAnimate>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                sx={{
                    mt: 1.5,
                    ml: 0.75,
                    width: 220,
                    "& .MuiMenuItem-root": { px: 1, typography: "body2", borderRadius: 0.75 },
                }}
            >
                <Typography variant="subtitle2" sx={{ px: 1.5, py: 1 }}>
                    Content Language
                </Typography>

                <Stack spacing={0.5}>
                    <ContentLanguageItem item={ENGLISH} selected={language === "en"} onSelect={handleSelectEnglish} />

                    <If value={languages.length > 0}>
                        <Divider sx={{ my: 0.5 }} />
                    </If>

                    <Repeat list={languages} item={<LanguageOption onClose={handleClose} />} />
                </Stack>

                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={handleAddLanguage} sx={{ color: "primary.main" }}>
                    <Iconify icon="mdi:plus" sx={{ mr: 1 }} />
                    Add Language
                </MenuItem>

                <If value={isAdmin && isTranslating}>
                    <MenuItem onClick={handleClearCache}>
                        <Iconify icon="mdi:trash-can-outline" sx={{ mr: 1, color: "text.disabled" }} />
                        Clear Cache ({languageName ?? language})
                    </MenuItem>
                </If>

                <If value={isAdmin}>
                    <MenuItem onClick={handleClearAllCaches}>
                        <Iconify icon="mdi:database-remove-outline" sx={{ mr: 1, color: "text.disabled" }} />
                        Clear All Translation Caches
                    </MenuItem>
                </If>
            </MenuPopover>
        </>
    )

    function handleOpen(event) {
        setOpen(event.currentTarget)
    }

    function handleClose() {
        setOpen(null)
    }

    function handleSelectEnglish() {
        setLanguage("en", false)
        handleClose()
    }

    async function handleAddLanguage() {
        handleClose()
        await openAddDialog()
    }

    async function handleClearCache() {
        handleClose()
        await clearTranslationCache({ language })
        enqueueSnackbar(`Translation cache cleared for ${languageName ?? language}`, { variant: "success" })
    }

    async function handleClearAllCaches() {
        handleClose()
        await clearTranslationCache({ language: undefined })
        enqueueSnackbar("All translation caches cleared", { variant: "success" })
    }
}
